import { ArrowLeft, Save } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from 'sonner'
import { useUserName, useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Checkbox } from "./ui/checkbox"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface NMXA172FormData {
  fecha: string
  hora: string
  gradosC: string
  porcentajeHR: string
  carga: string
  nivelVacio: string
  ruedaAbrasiva: string
  alturaBoquilla: string
  
  // Rotura del hilo
  hilo1: string
  hilo2: string
  hilo3: string
  hilo4: string
  hilo5: string
  resultadoHilo: string
  
  // Cambio de color
  color1: string
  color2: string
  color3: string
  color4: string
  color5: string
  resultadoColor: string
  
  // Frisas
  frisa1: string
  frisa2: string
  frisa3: string
  frisa4: string
  frisa5: string
  resultadoFrisa: string
  
  observaciones: string
  mostrarObservaciones: boolean
  realizo: string
  superviso: string
}

interface NMXA172FormProps {
  onBack: () => void
  pedidoId: string
  pedidoRowId?: string
  mode: 'create' | 'view' | 'edit'
  initialData?: Partial<NMXA172FormData>
  realizoUser?: string
  rol?: string // <-- NUEVO: rol del usuario
  onSave?: (data: NMXA172FormData) => void // <-- AGREGAR onSave opcional
  onRefreshBackend?: () => void;
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

export function NMXA172Form({ onBack, pedidoId, pedidoRowId, mode, initialData = {}, realizoUser, rol, onSave, onRefreshBackend, onAutosave }: NMXA172FormProps) {
  const globalRol = useUserRol();
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const userName = useUserName();
  const effectiveRol = rol || globalRol;

  const [formData, setFormData] = useState<NMXA172FormData>({
    fecha: "",
    hora: "",
    gradosC: "",
    porcentajeHR: "",
    carga: "",
    nivelVacio: "",
    ruedaAbrasiva: "",
    alturaBoquilla: "7 + 1 mm",
    
    hilo1: "",
    hilo2: "",
    hilo3: "",
    hilo4: "",
    hilo5: "",
    resultadoHilo: "",
    
    color1: "",
    color2: "",
    color3: "",
    color4: "",
    color5: "",
    resultadoColor: "",
    
    frisa1: "",
    frisa2: "",
    frisa3: "",
    frisa4: "",
    frisa5: "",
    resultadoFrisa: "",
    
    observaciones: "",
    realizo: "",
    superviso: "",
    ...initialData,
    mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
  })

  const [editMode, setEditMode] = useState(false)
  const [estatus, setEstatus] = useState<string>(initialData && (initialData as any).ACEPTADO ? (initialData as any).ACEPTADO : '')

  const pruebaNombre = (initialData as any)?.PRUEBA || '';

  const [pruebasSeleccionadas, setPruebasSeleccionadas] = useState({
    hilo: true,
    color: true,
    frisa: true,
  });

  const handlePruebaChange = (prueba: 'hilo' | 'color' | 'frisa', checked: boolean) => {
    setPruebasSeleccionadas(prev => ({ ...prev, [prueba]: checked }));
  };

  useEffect(() => {
    if (mode === 'create') {
      setFormData(prev => ({ ...prev, realizo: realizoUser || "" }))
    }
  }, [realizoUser, mode])

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    setFormData(prev => ({ ...prev, ...initialData, mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true }))
    setEstatus((initialData as any)?.ACEPTADO || '')
    setEditMode(false)
  }, [initialData])

  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false)
    }
  }, [effectiveRol, mode])

  const handleInputChange = (field: keyof NMXA172FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (typeof onSave === 'function') {
        onSave(formData)
      } else {
        console.warn('onSave no está definido en NMXA172Form')
      }
    } finally {
      setGuardando(false)
    }
  }

  // Obtener ROW_ID y PRUEBA correctamente
  const [autosaveRowId, setAutosaveRowId] = useState<string>(
    (formData as any).ROW_ID || (initialData as any)?.ROW_ID || (initialData as any)?.row_id || ''
  )
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialData as any)?.nomId || '')
  const rowId = autosaveRowId;
  const nomId = autosaveNomId;
  const pedidoValue = (formData as any).PEDIDO || pedidoId || '';
  const pruebaValue = (formData as any).PRUEBA || (initialData as any)?.PRUEBA || '';

  // Estado para modal de rechazo y loading
  const [showRechazoModal, setShowRechazoModal] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  const handleAccion = async (accion: 'aceptado' | 'rechazado', razonRechazo?: string) => {
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-172?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-172?';
    if (!window.confirm(confirmMsg)) return;
    const payload: any = {
      ...formData,
      superviso: realizoUser || formData.superviso || '',
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion
    };
    if (accion === 'rechazado' && razonRechazo) {
      payload.RAZON_RECHAZO = razonRechazo;
    }
    try {
      if (accion === 'rechazado') setRechazoLoading(true);
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, accion === 'rechazado' ? razonRechazo : undefined, realizoUser || formData.superviso || '', nomId);
      setEstatus(accion === 'aceptado' ? 'aceptado' : 'rechazado')
      setEditMode(false)
      setShowRechazoModal(false)
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err))
    } finally {
      if (accion === 'rechazado') setRechazoLoading(false);
    }
  }

  const handleEditar = () => {
    setEditMode(true)
  }

  const handleGuardarEdicion = async () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-172? Esta acción no se puede deshacer.')) return;
    const payload = {
      ...formData,
      superviso: realizoUser || formData.superviso || '',
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado'
    };
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  }

  // Lógica robusta para saber si la prueba está aceptada o rechazada
  const aceptadoRaw = (initialData as any)?.ACEPTADO ?? (initialData as any)?.aceptado ?? estatus;
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : '';
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado';
  const isReadOnly = mode === 'create' ? false : (mode === 'view' && !editMode);

  const formPayload = () => {
    const { ROW_ID, PRUEBA, PEDIDO, ...rest } = formData as any
    return rest
  }

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-172-INNTEX-2012 RESISTENCIA A LA ABRASIÓN PLANA TABER',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onBack()
  }

  // Utilidad para truncar a 1 decimal sin redondear
  function truncate1Decimal(num: number | string): string {
    const n = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(n)) return '';
    return (Math.trunc(n * 10) / 10).toFixed(1);
  }

  function calcPromedio(valores: number[]): string {
    if (!valores.length) return '';
    const avg = valores.reduce((a, b) => a + b, 0) / valores.length;
    return parseFloat(avg.toFixed(2)).toString();
  }

  // Calcular promedio para cada resultado
  useEffect(() => {
    // Rotura del hilo
    const hilos = [formData.hilo1, formData.hilo2, formData.hilo3, formData.hilo4, formData.hilo5]
      .map(v => parseFloat(v)).filter(v => !isNaN(v));
    const promedioHilo = calcPromedio(hilos);

    // Cambio de color
    const colores = [formData.color1, formData.color2, formData.color3, formData.color4, formData.color5]
      .map(v => parseFloat(v)).filter(v => !isNaN(v));
    const promedioColor = calcPromedio(colores);

    // Frisas
    const frisas = [formData.frisa1, formData.frisa2, formData.frisa3, formData.frisa4, formData.frisa5]
      .map(v => parseFloat(v)).filter(v => !isNaN(v));
    const promedioFrisa = calcPromedio(frisas);

    setFormData(prev => ({
      ...prev,
      resultadoHilo: promedioHilo,
      resultadoColor: promedioColor,
      resultadoFrisa: promedioFrisa
    }));
  }, [formData.hilo1, formData.hilo2, formData.hilo3, formData.hilo4, formData.hilo5, formData.color1, formData.color2, formData.color3, formData.color4, formData.color5, formData.frisa1, formData.frisa2, formData.frisa3, formData.frisa4, formData.frisa5]);

  // Helper para saber si una sección tiene datos
  const tieneDatos = {
    hilo: [formData.hilo1, formData.hilo2, formData.hilo3, formData.hilo4, formData.hilo5, formData.resultadoHilo].some(v => v && v !== ""),
    color: [formData.color1, formData.color2, formData.color3, formData.color4, formData.color5, formData.resultadoColor].some(v => v && v !== ""),
    frisa: [formData.frisa1, formData.frisa2, formData.frisa3, formData.frisa4, formData.frisa5, formData.resultadoFrisa].some(v => v && v !== ""),
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" size="icon" onClick={handleBackFlush}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              NMX-A-172-INNTEX-2012
            </h1>
            <p className="text-muted-foreground">
              DETERMINACIÓN DE LA RESISTENCIA A LA ABRASIÓN PLANA “TABER”
            </p>
            <p className="text-sm text-primary font-medium mt-1">Método: MET-7.2-14</p>
          </div>
        </div>

        {/* Información del Pedido */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-primary">Información del Pedido</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Pedido No: {pedidoId}</p>
          </CardContent>
        </Card>

        {/* Selector de pruebas a evaluar */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-primary">Pruebas a Evaluar</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="prueba-hilo"
                  checked={pruebasSeleccionadas.hilo}
                  onCheckedChange={checked => handlePruebaChange('hilo', !!checked)}
                />
                <Label htmlFor="prueba-hilo">Rotura del hilo</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="prueba-color"
                  checked={pruebasSeleccionadas.color}
                  onCheckedChange={checked => handlePruebaChange('color', !!checked)}
                />
                <Label htmlFor="prueba-color">Cambio de color</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="prueba-frisa"
                  checked={pruebasSeleccionadas.frisa}
                  onCheckedChange={checked => handlePruebaChange('frisa', !!checked)}
                />
                <Label htmlFor="prueba-frisa">Frisas</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Datos de la Solicitud */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-primary">Datos de la Solicitud</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="numero_solicitud">Número de solicitud</Label>
                <Input
                  id="numero_solicitud"
                  type="text"
                  placeholder="Ejemplo: 12345"
                  value={(formData as any).numero_solicitud || ""}
                  onChange={e => handleInputChange('numero_solicitud' as any, e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label htmlFor="id_termohigrometro">ID de termohigrómetro</Label>
                <Select
                  value={(formData as any).id_termohigrometro || ""}
                  onValueChange={value => handleInputChange('id_termohigrometro' as any, value)}
                  disabled={isReadOnly}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecciona..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="E-59">E-59</SelectItem>
                    <SelectItem value="E-57">E-57</SelectItem>
                    <SelectItem value="E-56">E-56</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Parámetros de Prueba */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-primary">Parámetros de Prueba</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border border-border">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left font-medium">FECHA:</th>
                    <th className="border border-border p-3 text-left font-medium">HORA:</th>
                    <th className="border border-border p-3 text-left font-medium">°C:</th>
                    <th className="border border-border p-3 text-left font-medium">% HR:</th>
                    <th className="border border-border p-3 text-left font-medium">CARGA (g):</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-2">
                      <Input
                        type="date"
                        value={formData.fecha}
                        onChange={(e) => handleInputChange('fecha', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border border-border p-2">
                      <Input
                        type="time"
                        value={formData.hora}
                        onChange={(e) => { if (e.target.value) handleInputChange('hora', e.target.value) }}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border border-border p-2">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="°C"
                        value={formData.gradosC}
                        onChange={(e) => handleInputChange('gradosC', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border border-border p-2">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="%"
                        value={formData.porcentajeHR}
                        onChange={(e) => handleInputChange('porcentajeHR', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border border-border p-2">
                      <Select
                        value={formData.carga || ""}
                        onValueChange={value => handleInputChange('carga', value)}
                        disabled={isReadOnly}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Selecciona..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="250">250</SelectItem>
                          <SelectItem value="500">500</SelectItem>
                          <SelectItem value="1000">1000</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full border border-border">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left font-medium">NIVEL DE VACÍO DE SUCCIÓN:</th>
                    <th className="border border-border p-3 text-left font-medium">RUEDA ABRASIVA:</th>
                    <th className="border border-border p-3 text-left font-medium">ALTURA DE LA BOQUILLA 7 + 1 mm</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-2">
                      <Input
                        type="text"
                        placeholder="Nivel de vacío"
                        value={formData.nivelVacio}
                        onChange={(e) => handleInputChange('nivelVacio', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border border-border p-2">
                      <Select
                        value={formData.ruedaAbrasiva || ""}
                        onValueChange={value => handleInputChange('ruedaAbrasiva', value)}
                        disabled={isReadOnly}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Selecciona..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CS-10">CS-10</SelectItem>
                          <SelectItem value="H-18">H-18</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="border border-border p-2">
                      <Input
                        type="text"
                        value={formData.alturaBoquilla}
                        onChange={(e) => handleInputChange('alturaBoquilla', e.target.value)}
                        disabled={isReadOnly}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Tablas de Resultados */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {(mode !== 'view' || tieneDatos.hilo) && pruebasSeleccionadas.hilo && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-center text-primary bg-accent p-2 rounded">
                  ROTURA DEL HILO
                </CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full border border-border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border border-border p-2 text-center font-medium">NO.</th>
                      <th className="border border-border p-2 text-center font-medium">CICLOS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3, 4, 5].map((num) => (
                      <tr key={num}>
                        <td className="border border-border p-2 text-center font-medium bg-muted">
                          {num}
                        </td>
                        <td className="border border-border p-1">
                          <Input
                            type="number"
                            placeholder="Ciclos"
                            value={formData[`hilo${num}` as keyof NMXA172FormData] as string}
                            onChange={(e) => handleInputChange(`hilo${num}` as keyof NMXA172FormData, e.target.value)}
                            disabled={isReadOnly}
                            className="text-center border-0 bg-transparent"
                          />
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-accent">
                      <td className="border border-border p-2 text-center font-medium">
                        RESULTADO
                      </td>
                      <td className="border border-border p-1">
                        <Input
                          type="text"
                          placeholder="Resultado"
                          value={formData.resultadoHilo}
                          onChange={(e) => handleInputChange('resultadoHilo', e.target.value)}
                          disabled={isReadOnly}
                          className="text-center border-0 bg-transparent font-medium"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {(mode !== 'view' || tieneDatos.color) && pruebasSeleccionadas.color && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-center text-primary bg-accent p-2 rounded">
                  CAMBIO DE COLOR
                </CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full border border-border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border border-border p-2 text-center font-medium">NO.</th>
                      <th className="border border-border p-2 text-center font-medium">CICLOS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3, 4, 5].map((num) => (
                      <tr key={num}>
                        <td className="border border-border p-2 text-center font-medium bg-muted">
                          {num}
                        </td>
                        <td className="border border-border p-1">
                          <Input
                            type="number"
                            placeholder="Ciclos"
                            value={formData[`color${num}` as keyof NMXA172FormData] as string}
                            onChange={(e) => handleInputChange(`color${num}` as keyof NMXA172FormData, e.target.value)}
                            disabled={isReadOnly}
                            className="text-center border-0 bg-transparent"
                          />
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-accent">
                      <td className="border border-border p-2 text-center font-medium">
                        RESULTADO
                      </td>
                      <td className="border border-border p-1">
                        <Input
                          type="text"
                          placeholder="Resultado"
                          value={formData.resultadoColor}
                          onChange={(e) => handleInputChange('resultadoColor', e.target.value)}
                          disabled={isReadOnly}
                          className="text-center border-0 bg-transparent font-medium"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {(mode !== 'view' || tieneDatos.frisa) && pruebasSeleccionadas.frisa && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-center text-primary bg-accent p-2 rounded">
                  FRISAS
                </CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full border border-border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border border-border p-2 text-center font-medium">NO.</th>
                      <th className="border border-border p-2 text-center font-medium">CICLOS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3, 4, 5].map((num) => (
                      <tr key={num}>
                        <td className="border border-border p-2 text-center font-medium bg-muted">
                          {num}
                        </td>
                        <td className="border border-border p-1">
                          <Input
                            type="number"
                            placeholder="Ciclos"
                            value={formData[`frisa${num}` as keyof NMXA172FormData] as string}
                            onChange={(e) => handleInputChange(`frisa${num}` as keyof NMXA172FormData, e.target.value)}
                            disabled={isReadOnly}
                            className="text-center border-0 bg-transparent"
                          />
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-accent">
                      <td className="border border-border p-2 text-center font-medium">
                        RESULTADO
                      </td>
                      <td className="border border-border p-1">
                        <Input
                          type="text"
                          placeholder="Resultado"
                          value={formData.resultadoFrisa}
                          onChange={(e) => handleInputChange('resultadoFrisa', e.target.value)}
                          disabled={isReadOnly}
                          className="text-center border-0 bg-transparent font-medium"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Observaciones y Firmas */}
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-primary">Observaciones y Firmas</CardTitle>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Mostrar en Reporte Cliente
              <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
            </span>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="observaciones">OBSERVACIONES:</Label>
              <Textarea
                id="observaciones"
                placeholder="Ingrese las observaciones aquí..."
                value={formData.observaciones}
                onChange={(e) => handleInputChange('observaciones', e.target.value)}
                disabled={isReadOnly}
                className="mt-2 min-h-[100px]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="realizo">REALIZÓ:</Label>
                <Input
                  id="realizo"
                  type="text"
                  placeholder="Nombre de quien realizó la prueba"
                  value={formData.realizo}
                  readOnly
                  className="mt-2 bg-muted-foreground/10 cursor-not-allowed"
                />
              </div>
              <div>
                <Label htmlFor="superviso">SUPERVISÓ:</Label>
                <Input
                  id="superviso"
                  type="text"
                  placeholder="Nombre de quien supervisó"
                  value={formData.superviso}
                  readOnly
                  className="mt-2 bg-muted-foreground/10 cursor-not-allowed"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={handleBackFlush}>
            Cancelar
          </Button>
          {/* Botones de acciones en modo view y no aceptado/rechazado SOLO PARA ADMIN */}
          {mode === 'view' && !editMode && puedeRevisar && !isAceptado && (
            <>
              <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
              <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
              <Button variant="secondary" onClick={handleEditar}>Editar</Button>
            </>
          )}
          {/* Botón guardar edición SOLO PARA ADMIN */}
          {mode === 'view' && editMode && puedeRevisar && !isAceptado && (
            <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
          )}
          {/* Botón guardar en modo create */}
          {mode === 'create' && (
            <Button onClick={handleSave} disabled={guardando}>
              <Save className="h-4 w-4 mr-2" />
              {guardando ? 'Guardando...' : 'Guardar Formulario'}
            </Button>
          )}
        </div>
        <RechazoModal
          open={showRechazoModal}
          onClose={() => setShowRechazoModal(false)}
          loading={rechazoLoading}
          onSubmit={(razon) => handleAccion('rechazado', razon)}
        />
      </div>
    </div>
  )
}